"use client";
// ===================================================
// GAM Command Center — the internal chat surface (משטח הצ׳אט הפנימי).
//
// One component, two presentations: the full page and the floating dock widget.
// Built fresh rather than extended from AiChatArea — that component is 525 lines shaped
// around an AI conversation (modes, personas, streaming, regenerate, agent switching), and
// a messaging channel would have to pass ~15 inert props to borrow it. What IS borrowed is
// the visual language: Brand v2 teal on dark, hairline separators, Heebo. Same call
// CLAUDE.md §14 made for the conversation board.
//
// RTL: every message body carries dir="auto" so a Hebrew line flows right-to-left and an
// English line left-to-right inside the same transcript — the thing Slack gets wrong and
// Gal feels daily.
//
// Spec: docs/specs/chat-substrate-stage1-internal-alerts.md §5.3
// ===================================================
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Hash, Send, Bell, Loader2 } from "lucide-react";
import {
  fetchAgents,
  fetchMessages,
  sendMessage,
  subscribeToMessages,
  unsubscribe,
  ALERTS_CHANNEL_TITLE,
  type ChatAgent,
  type ChatMessage,
} from "@/lib/supabase/chatQueries";
import { useChatNotifications } from "@/contexts/ChatNotificationsContext";
import { supabase } from "@/lib/supabaseClient";

type Mode = "full" | "floating";

function dayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const same = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (same(d, today)) return "היום";
  if (same(d, yesterday)) return "אתמול";
  return d.toLocaleDateString("he-IL", { day: "numeric", month: "long" });
}

const timeLabel = (iso: string) =>
  new Date(iso).toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" });

export function ChatSurface({ mode = "full" }: { mode?: Mode }) {
  const { conversations, unread, markSeen, lastMessage } = useChatNotifications();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [agents, setAgents] = useState<ChatAgent[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selfId, setSelfId] = useState<string | null>(null);

  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void supabase.auth.getUser().then(({ data }) => setSelfId(data.user?.id ?? null));
    void fetchAgents().then(setAgents).catch(() => setAgents([]));
  }, []);

  // Default to the alerts channel — it is the reason this surface exists.
  useEffect(() => {
    if (activeId || conversations.length === 0) return;
    const alerts = conversations.find((c) => c.title === ALERTS_CHANNEL_TITLE);
    setActiveId(alerts?.id ?? conversations[0].id);
  }, [conversations, activeId]);

  // Load the transcript for the selected channel.
  useEffect(() => {
    if (!activeId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchMessages(activeId)
      .then((msgs) => {
        if (cancelled) return;
        setMessages(msgs);
        markSeen(activeId);
      })
      .catch((e: unknown) => !cancelled && setError(e instanceof Error ? e.message : "load_failed"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [activeId, markSeen]);

  // Live append for the open channel.
  useEffect(() => {
    if (!activeId) return;
    const channel = subscribeToMessages(activeId, (msg) => {
      setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
      markSeen(activeId);
    });
    return () => unsubscribe(channel);
  }, [activeId, markSeen]);

  // The provider's socket may deliver a message for the open channel a beat before ours
  // connects; fold it in rather than waiting for a refetch.
  useEffect(() => {
    if (!lastMessage || lastMessage.conversation_id !== activeId) return;
    setMessages((prev) => (prev.some((m) => m.id === lastMessage.id) ? prev : [...prev, lastMessage]));
  }, [lastMessage, activeId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  const agentName = useCallback(
    (id: string | null) => agents.find((a) => a.id === id)?.display_name ?? "מערכת",
    [agents]
  );

  const handleSend = useCallback(async () => {
    const body = input.trim();
    if (!body || !activeId || sending) return;
    setSending(true);
    setError(null);
    try {
      const msg = await sendMessage(activeId, body);
      setInput("");
      setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "send_failed");
    } finally {
      setSending(false);
    }
  }, [input, activeId, sending]);

  // Group by day, and collapse a run from the same sender into one avatar block.
  const grouped = useMemo(() => {
    const out: { day: string; runs: { senderKey: string; msgs: ChatMessage[] }[] }[] = [];
    for (const m of messages) {
      const day = dayLabel(m.created_at);
      let bucket = out[out.length - 1];
      if (!bucket || bucket.day !== day) {
        bucket = { day, runs: [] };
        out.push(bucket);
      }
      const key = `${m.sender_type}:${m.sender_id}`;
      const lastRun = bucket.runs[bucket.runs.length - 1];
      if (lastRun && lastRun.senderKey === key) lastRun.msgs.push(m);
      else bucket.runs.push({ senderKey: key, msgs: [m] });
    }
    return out;
  }, [messages]);

  const active = conversations.find((c) => c.id === activeId);
  const compact = mode === "floating";

  return (
    <div className="flex h-full min-h-0 w-full overflow-hidden rounded-2xl border border-white/10 bg-slate-950/60" dir="rtl">
      {/* ── channel list (right, RTL) ── */}
      <aside
        className={`shrink-0 border-l border-white/10 bg-white/[0.03] ${compact ? "w-36" : "w-60"}`}
      >
        <div className="px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          ערוצים
        </div>
        <nav className="space-y-0.5 px-2 pb-2">
          {conversations.map((c) => {
            const n = unread[c.id] ?? 0;
            const isActive = c.id === activeId;
            const isAlerts = c.title === ALERTS_CHANNEL_TITLE;
            return (
              <button
                key={c.id}
                onClick={() => setActiveId(c.id)}
                className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-right text-sm transition ${
                  isActive ? "bg-teal-500/15 text-teal-100" : "text-slate-300 hover:bg-white/5"
                }`}
              >
                {isAlerts ? (
                  <Bell className="size-3.5 shrink-0 opacity-70" />
                ) : (
                  <Hash className="size-3.5 shrink-0 opacity-70" />
                )}
                <span className="min-w-0 flex-1 truncate">{c.title ?? "ללא שם"}</span>
                {n > 0 && (
                  <span className="shrink-0 rounded-full bg-teal-500 px-1.5 text-[10px] font-bold text-slate-950">
                    {n > 99 ? "99+" : n}
                  </span>
                )}
              </button>
            );
          })}
          {conversations.length === 0 && (
            <p className="px-2.5 py-3 text-xs leading-relaxed text-slate-500">
              אין ערוצים גלויים. ייתכן שאינך חבר באף ערוץ.
            </p>
          )}
        </nav>
      </aside>

      {/* ── transcript + composer ── */}
      <section className="flex min-h-0 min-w-0 flex-1 flex-col">
        {!compact && (
          <header className="flex items-center gap-2 border-b border-white/10 px-4 py-2.5">
            <Hash className="size-4 text-teal-400/70" />
            <h2 className="text-sm font-semibold text-slate-100">{active?.title ?? "צ׳אט"}</h2>
            {active?.audience === "internal" && (
              <span className="rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] text-slate-400">
                פנימי
              </span>
            )}
          </header>
        )}

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-3">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-slate-500">
              <Loader2 className="size-4 animate-spin" />
              טוען
            </div>
          )}

          {!loading && messages.length === 0 && (
            <p className="py-8 text-center text-sm text-slate-500">אין הודעות בערוץ הזה עדיין.</p>
          )}

          {grouped.map((bucket) => (
            <div key={bucket.day} className="space-y-3">
              <div className="flex items-center gap-3">
                <span className="h-px flex-1 bg-white/10" />
                <span className="text-[11px] text-slate-500">{bucket.day}</span>
                <span className="h-px flex-1 bg-white/10" />
              </div>

              {bucket.runs.map((run, i) => {
                const first = run.msgs[0];
                const isAgent = first.sender_type === "agent";
                const isSelf = first.sender_id === selfId;
                return (
                  <div key={`${bucket.day}-${i}`} className="flex gap-2.5">
                    <div
                      className={`mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg text-[11px] font-bold ${
                        isAgent
                          ? "bg-amber-500/20 text-amber-200"
                          : isSelf
                            ? "bg-teal-500/20 text-teal-200"
                            : "bg-white/10 text-slate-300"
                      }`}
                    >
                      {isAgent ? <Bell className="size-3.5" /> : isSelf ? "אני" : "•"}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs font-semibold text-slate-200">
                          {isAgent ? agentName(first.sender_id) : isSelf ? "אני" : "צוות"}
                        </span>
                        <span className="text-[10px] text-slate-500">{timeLabel(first.created_at)}</span>
                      </div>
                      {run.msgs.map((m) => (
                        <p
                          key={m.id}
                          dir="auto"
                          className={`whitespace-pre-wrap break-words text-sm leading-relaxed ${
                            isAgent ? "text-amber-100/90" : "text-slate-300"
                          }`}
                        >
                          {m.body}
                        </p>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
          <div ref={endRef} />
        </div>

        {error && (
          <div className="mx-4 mb-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-1.5 text-xs text-rose-200">
            {error}
          </div>
        )}

        <div className="flex items-end gap-2 border-t border-white/10 p-3">
          <textarea
            dir="auto"
            rows={compact ? 1 : 2}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void handleSend();
              }
            }}
            placeholder="כתוב הודעה…"
            className="min-h-9 flex-1 resize-none rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-teal-500/40 focus:outline-none"
          />
          <button
            onClick={() => void handleSend()}
            disabled={!input.trim() || sending || !activeId}
            aria-label="שלח"
            className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-teal-500 text-slate-950 transition hover:bg-teal-400 disabled:cursor-not-allowed disabled:opacity-30"
          >
            {sending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4 -scale-x-100" />}
          </button>
        </div>
      </section>
    </div>
  );
}
